import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import BillComponent from "./BillComponent";
import useDetectPrint from "../../hooks/useDetectPrint";
import { isMealBillAndroid, androidPrinter } from "../../native/mealbill-android.mjs";
import { useSelector } from "react-redux";
import { categoryItems, planKotStations, sendKotJobs } from "../../native/kot-stations.mjs";
import moment from "moment";

/**
 * Build a flat ESC/POS receipt matching the KOTcomponent/BillComponent.js layout:
 * Header (restaurant, address, GST) → customer name → date + token → type + table
 * → Sr / Product / Qty table → Total → Remarks
 *
 * jobId format: "kot:<branchOrderNumber>:<kotIndex>" — stable per print event.
 */
function createAndroidKOTReceipt({ printData, restaurant, branchAddress, gstNumber, W }) {
  const lastKot = printData?.KOTS?.[printData.KOTS.length - 1];
  const kotIndex = printData?.KOTS?.length ?? 1;
  const branchOrderNumber = (printData?.branchOrderNumber ?? "").replace(/\s/g, "");
  const jobId = `kot:${branchOrderNumber}:${kotIndex}`.replace(/[^A-Za-z0-9._:-]/g, "-").slice(0, 128);

  const SEP = "-".repeat(W);
  const spreadEven = (...parts) => {
    const p = parts.filter(Boolean);
    if (!p.length) return "";
    if (p.length === 1) return p[0];
    const tl = p.reduce((s, x) => s + x.length, 0);
    const sp = W - tl;
    const gaps = p.length - 1;
    const spc = sp > 0 ? Math.floor(sp / gaps) : 1;
    const extra = sp > 0 ? sp - spc * gaps : 0;
    return p.map((x, i) => i < p.length - 1 ? x + " ".repeat(spc + (i < extra ? 1 : 0)) : x).join("");
  };

  const lines = [];

  // ── RESET: force printer to left-align before every job ───────────────────
  // Without this, the printer retains center-align from the previous job's
  // last separator line, causing the restaurant name to drift further left
  // with each subsequent print.
  lines.push({ text: "", align: "left" });

  // Header: Restaurant name + address, NO separator line after
  if (restaurant) lines.push({ text: restaurant, align: "center", bold: true });
  if (branchAddress) lines.push({ text: branchAddress, align: "center" });
  if (gstNumber) lines.push({ text: `GST ${gstNumber}`, align: "center" });

  // ── Info rows (no surrounding separators) ─────────────────────────────────
  const { customerName, customerMobile, orderType, tableNumber, remarks } = printData;
  const kotNumber = lastKot?.kotNumber;

  // Customer name row (only if present) — explicit left so it doesn't inherit center
  if (customerName) {
    lines.push({ text: `Name : ${customerName} ${customerMobile ? `(${customerMobile})` : ""}`, align: "left" });
  }

  // Row: date (left)  |  Token : N (right)
  lines.push({ columns: [moment().format("DD/MM/YYYY HH:mm"), kotNumber ? `Token : ${kotNumber}` : ""] });

  // Row: orderType (left)  |  Table : N (right)
  lines.push({ columns: [orderType ?? "", tableNumber ? `Table : ${tableNumber}` : ""] });

  // Separator BEFORE the item table
  lines.push({ text: SEP, align: "center" });

  // Item table header: #  Item | Qty
  lines.push({ columns: ["#  Item", "Qty"], bold: true });
  lines.push({ text: SEP, align: "center" });

  // ── Items ─────────────────────────────────────────────────────────────────
  let totalQty = 0;
  let sr = 0;
  (lastKot?.orderItems ?? []).forEach((item) => {
    const qty = item.quantity ?? 0;
    totalQty += qty;
    sr++;
    lines.push({ columns: [`${sr}  ${item.itemName ?? ""}`, String(qty)] });
    // Toppings row
    if (item?.activeTopping?.length > 0) {
      const toppingNames = item.activeTopping.map((t) => t.itemName).join(",");
      lines.push({ columns: ["-  " + toppingNames, ""] });
    }
  });

  // Separator after items
  lines.push({ text: SEP, align: "center" });

  // Total right-aligned
  lines.push({ columns: ["", `Total  ${totalQty}`], bold: true });

  // ── Remarks ───────────────────────────────────────────────────────────────
  if (remarks) {
    lines.push({ text: "", align: "left" });
    lines.push({ text: `Remarks : ${remarks}`, align: "left" });
  }

  return { jobId, printer: "kitchen", lines };
}

const PrintComponent = ({
  headers,
  footers,
  printData,
  restaurant,
  logo,
  gstNumber,
  containerStyle,
  receiptMessage,
  branchAddress,
  printSetting,
  menuItems = [],
  printKOTTokenSlip = false,
}) => {
  const isPrinting = useDetectPrint();
  const branchId = useSelector(state => state.user.branchId);
  const [stationResults, setStationResults] = useState([]);
  const [stationBusy, setStationBusy] = useState(false);
  const [statusDismissed, setStatusDismissed] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const retryStation = async (job) => {
    if (stationBusy || !window.confirm('Check this printer first. Print another copy for this station only?')) return;
    setStationBusy(true);
    const retry = { ...job, receipt: { ...job.receipt, jobId: `kot:${crypto.randomUUID()}:retry` } };
    const result = (await sendKotJobs([retry]))[0];
    if (mounted.current) { setStationResults(old => old.map(r => r === job ? result : r)); setStationBusy(false); }
  };

  const componentRef = useRef();
  const lastPrintData = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      // alert("print completed");
    },
  });

  useEffect(() => {
    if (isPrinting || !printData) return;

    // ── Android native thermal print path ──────────────────────────────────
    if (isMealBillAndroid()) {
      if (lastPrintData.current === printData) return; // Deduplicate Strict Mode double renders
      lastPrintData.current = printData;

      setStationBusy(true); setStationResults([]); setStatusDismissed(false);
      (async () => {
        if (!branchId) throw new Error('Branch is not loaded. Try again after the order screen loads.');
        const caps = await androidPrinter.capabilities();
        if (!caps.kotStationsVersion) throw new Error('Install test APK 1.0.0.11 for station KOT printing.');
        const [{ rows }, { profiles }] = await Promise.all([
          androidPrinter.getKotMappings(String(branchId)), androidPrinter.getProfiles()
        ]);
        const lastKot = printData.KOTS?.[printData.KOTS.length - 1];
        if (!lastKot || !Array.isArray(lastKot.orderItems)) throw new Error('No completed KOT items to print.');
        const plan = planKotStations(categoryItems(lastKot.orderItems, menuItems), rows, profiles,
          printData.orderType === 'Parcel' && printKOTTokenSlip);
        if (plan.destination === 'manager') return; // Preserve the supplied upstream Manager flow; never re-create a KOT.
        const eventId = crypto.randomUUID();
        const jobs = plan.groups.map((group, index) => {
          const stationData = { ...printData, KOTS: [...printData.KOTS.slice(0, -1), { ...lastKot, orderItems: group.items }] };
          const receipt = group.tokenSlip
            ? { lines: [{ text: String(lastKot.kotNumber ?? ''), align: 'center', bold: true, size: 48 }] }
            : createAndroidKOTReceipt({ printData: stationData, restaurant, branchAddress, gstNumber, W: group.profile.width === 384 ? 32 : 42 });
          return { label: group.label, receipt: { ...receipt, printer: group.printer, destination: group.profile, jobId: `kot:${eventId}:${index}` } };
        });
        const results = await sendKotJobs(jobs);
        if (mounted.current) setStationResults(results);
      })().catch(error => {
        if (mounted.current) setStationResults([{ label: 'KOT', state: 'failed', message: error.message }]);
      }).finally(() => { if (mounted.current) setStationBusy(false); });
      return; // Never fall back to browser print after native attempt
    }

    // ── Electron desktop path ───────────────────────────────────────────────
    console.log("isElectron", window.api);
    if (window?.api?.isElectron) {
      console.log("isElectron inside", window.api);
      window.api.send(printData);
    } else {
      // ── Browser fallback (print dialog) ──────────────────────────────────
      handlePrint();
    }
  }, [printData, isPrinting]);

  return (
    <div style={containerStyle}>
      {isMealBillAndroid() && !statusDismissed && (stationBusy || stationResults.some(job => job.state !== 'sent')) && <div role="status" aria-live="polite"
        style={{ position: 'fixed', left: 8, right: 8, bottom: 72, zIndex: 3000, background: '#fff', border: '1px solid #ced4da', borderRadius: 6, padding: '8px 10px', boxShadow: '0 2px 10px rgba(0,0,0,.25)', fontSize: 13 }}>
        {stationBusy && <div>Sending KOTs...</div>}
        {stationResults.map((job, index) => <div key={index}>
          {job.label}: {job.state === 'sent' ? 'Sent' : job.message}
          {job.receipt && job.state !== 'sent' && <button type="button" className="btn btn-sm btn-outline-primary ml-2" disabled={stationBusy} onClick={() => retryStation(job)}>Retry this station</button>}
        </div>)}
        {!stationBusy && <button type="button" className="btn btn-sm btn-link p-0 mt-1" onClick={() => setStatusDismissed(true)}>Dismiss</button>}
      </div>}
      <div style={{ display: "none" }}>
        <BillComponent
          ref={componentRef}
          orderData={printData}
          restaurant={restaurant}
          logo={logo}
          branchAddress={branchAddress}
        />
      </div>
    </div>
  );
};
export default PrintComponent;
